import { useEffect, useRef } from 'react';
import type { Awaitable } from '../utils/types/Awaitable';
import { useStableCallback } from './useStableCallback';

export const DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT = 300;

export interface DebounceOptions {
  /**
   * Wait for async callbacks to finish before another callback trigger
   *
   * When debouncing an async callback, we should also wait for the async function
   * to finish to prevent e.g. multiple slow requests running in parallel.
   *
   * Without this flag, useDebounce debounces just regular javascript function
   * execution and does not take care whether it returns some pending promise.
   *
   * With this flag turned on, once the debounced callback is triggered, it checks
   * whether there isn't a pending promise from previous call. If there is,
   * last trigger of the callback is enqueued to be called when currently
   * running one resolves (discarding any previously enqueued).
   *
   * (callback trigger = when the timer runs out and the function is going to be actually called)
   */
  waitForAsync?: boolean;
}

interface RunningAsyncCallback {
  runningPromise: Awaitable<void>;
  next?: () => Awaitable<void>;
}

/**
 * Create a debounced function handler
 *
 * Example:
 * ```
 * const debounceClick = useDebounce(500);
 * const handleClick = debounceClick(() => console.log('clicked!'));
 * ```
 */
export const useDebounce = (
  interval: number,
  options?: DebounceOptions,
): ((callback: () => Awaitable<void>) => void) => {
  const timeout = useRef<number | undefined>();
  const running = useRef<RunningAsyncCallback | null>(null);
  useEffect(
    () => () => {
      window.clearTimeout(timeout.current);
    },
    [],
  );
  return useStableCallback((callback) => {
    const callbackWithWait = options?.waitForAsync
      ? async () => {
          // While promise returned from last callback is still pending, any subsequent
          // attemp to call the debounced function is redirected to be just put on hold
          // into running.current.next (so only the callback from last executed attempt
          // is stored here).
          if (running.current) {
            running.current.next = callback;
            return;
          }
          // Call the passed callback and check for next enqueued at the end.
          // If there is an enqueued callback, process it the same way.
          let callbackToProcess: (() => Awaitable<void>) | undefined = callback;
          while (callbackToProcess) {
            running.current = {
              runningPromise: callbackToProcess(),
            };

            await running.current.runningPromise;
            callbackToProcess = running.current?.next;
            running.current = null;
          }
        }
      : callback;
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(callbackWithWait, interval);
  });
};
